/**
 * Illustration cache utilities.
 *
 * Registers the service worker and provides a function to prefetch
 * illustration URLs in bulk once the offers data is loaded.
 */

const SW_PATH = '/sw.js'

/** Register the illustration-caching service worker. */
export function registerIllustrationSW() {
  if (!('serviceWorker' in navigator)) return

  navigator.serviceWorker.register(SW_PATH).catch((err) => {
    console.warn('[SW] Registration failed:', err.message)
  })
}

/**
 * Prefetch illustration URLs via the service worker.
 *
 * Call this after offers load to warm the cache for visible illustrations.
 * The SW handles deduplication — already-cached URLs are skipped.
 *
 * @param {string[]} urls — illustration URLs to prefetch (e.g. ['/api/illustration/42-broccoli.png'])
 */
export function prefetchIllustrations(urls) {
  if (!urls.length) return
  if (!navigator.serviceWorker?.controller) {
    // SW not yet active — fall back to <link rel="prefetch"> hints
    prefetchViaLinkHints(urls)
    return
  }

  navigator.serviceWorker.controller.postMessage({
    type: 'prefetch-illustrations',
    urls,
  })
}

/**
 * Fallback: insert <link rel="prefetch"> tags for illustration URLs.
 * Used when the service worker isn't active yet (first visit).
 */
function prefetchViaLinkHints(urls) {
  const fragment = document.createDocumentFragment()

  for (const url of urls) {
    // Avoid duplicates
    if (document.querySelector(`link[rel="prefetch"][href="${url}"]`)) continue

    const link = document.createElement('link')
    link.rel = 'prefetch'
    link.as = 'image'
    link.href = url
    fragment.appendChild(link)
  }

  document.head.appendChild(fragment)
}
